/**
 * HelpfulGuides (public)
 *
 * Small internal-linking section used on:
 * - Home page
 * - Custom Cakes section (Order page)
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

type Guide = {
  id: string;
  title: string;
  slug: string;
  excerpt: string | null;
  published_at: string | null;
};

type Props = {
  title?: string;
  limit?: number;
  className?: string;
};

export default function HelpfulGuides({ title = 'Helpful Guides', limit = 3, className }: Props) {
  const [guides, setGuides] = useState<Guide[]>([]);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`/api/articles?limit=${encodeURIComponent(String(limit))}`);
        if (!res.ok) return;
        const data = await res.json();
        setGuides((data.articles || []).slice(0, limit));
      } catch {
        // Non-fatal; the section can be hidden if fetch fails.
      }
    };
    load();
  }, [limit]);

  if (!guides.length) return null;

  return (
    <section className={className}>
      <div className="container-custom">
        <div className="flex items-end justify-between gap-4 mb-6">
          <div>
            <h2 className="text-2xl font-display font-bold text-gray-900">{title}</h2>
            <p className="text-gray-600 mt-1">Quick tips to help you order with confidence.</p>
          </div>
          <Link to="/guides" className="text-purple-700 hover:text-purple-800 font-semibold">
            View all →
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {guides.map((g) => (
            <Link
              key={g.id}
              to={`/guides/${g.slug}`}
              className="bg-white rounded-2xl border border-gray-200 p-5 hover:border-purple-300 hover:shadow-md transition-all"
            >
              <div className="text-lg font-semibold text-gray-900">{g.title}</div>
              {g.excerpt && <p className="text-gray-600 mt-2 line-clamp-3">{g.excerpt}</p>}
              <div className="mt-4 text-sm text-purple-700 font-medium">Read guide →</div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}


